import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faCircleCheck,
  faCircleExclamation,
  faClipboardList,
  faHourglassHalf,
  faSearch,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

interface ICustomerSearch {
  inputRef?: React.MutableRefObject<HTMLInputElement>;
  hasSearch?: boolean;
  onSearch: (value: string) => void;
  onClear: () => void;
}

const STATUS_SHORTCUTS = [
  {
    label: "Awaiting customer",
    route: "/chase/Awaiting-Customer",
    icon: faHourglassHalf,
  },
  {
    label: "Pending review",
    route: "/chase/Pending-Review",
    icon: faClipboardList,
  },
  { label: "Rejected", route: "/chase/Rejected", icon: faCircleExclamation },
  { label: "Approved", route: "/chase/Approved", icon: faCircleCheck },
];

const CustomerSearch: React.FC<ICustomerSearch> = ({
  inputRef,
  hasSearch,
  onSearch,
  onClear,
}) => {
  const navigate = useNavigate();
  const [value, setValue] = useState<string>("");

  // ---------------------------------------- [ SUBMIT SEARCH ] -------------------------------
  const HandleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const search = (inputRef?.current?.value ?? value).trim();
    if (!search) return;
    onSearch(search);
  };

  // ---------------------------------------- [ CLEAR SEARCH ] --------------------------------
  const HandleClear = () => {
    setValue("");
    if (inputRef?.current) inputRef.current.value = "";
    onClear();
  };

  return (
    <div className="relative overflow-hidden mt-5 mr-1 smallTablet:mr-0 bg-surface border border-edge rounded-2xl shadow-card px-8 py-8 smallTablet:px-4 smallTablet:py-6 animate-fade-in">
      {/* Faint background glow */}
      <div className="pointer-events-none absolute -top-24 -right-16 h-64 w-64 rounded-full bg-accent/10 blur-3xl" />

      <div className="relative">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 shrink-0 rounded-xl bg-gradient-to-br from-accent to-accent-bright flex items-center justify-center shadow-glow-sm">
            <FontAwesomeIcon icon={faSearch} className="text-[14px] text-white" />
          </div>
          <div className="min-w-0">
            <h2 className="text-[22px] font-dmSans font-bold text-text-primary leading-tight">
              Find a customer
            </h2>
            <p className="mt-0.5 text-[13px] font-medium text-light-gray">
              Look up a chase to review documents, update items or message the customer.
            </p>
          </div>
        </div>

        <form
          onSubmit={HandleSubmit}
          className="mt-6 flex items-stretch gap-3 smallTablet:flex-col"
        >
          <div className="group h-14 flex-1 flex items-center rounded-xl bg-input-bg border border-input-border focus-within:border-indigo-400/50 focus-within:ring-4 focus-within:ring-indigo-400/15 transition-all duration-200 px-5">
            <FontAwesomeIcon
              icon={faSearch}
              className="text-[16px] text-light-gray group-focus-within:text-accent mr-3.5 shrink-0 transition-colors"
            />
            <input
              ref={inputRef}
              type="text"
              aria-label="Search customers"
              placeholder="Email, name or policy reference"
              className="flex-grow min-w-0 bg-inherit text-[15px] text-text-primary placeholder:text-light-gray font-medium focus:outline-none"
              onChange={(e) => setValue(e.target.value)}
            />
            {(value || hasSearch) && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={HandleClear}
                className="ml-2 h-7 w-7 shrink-0 rounded-full flex items-center justify-center text-light-gray hover:text-text-primary hover:bg-overlay-hover transition-colors"
              >
                <FontAwesomeIcon icon={faXmark} className="text-[13px]" />
              </button>
            )}
          </div>
          <button
            type="submit"
            disabled={!value.trim()}
            className="h-14 px-7 flex items-center justify-center gap-3 rounded-xl text-[15px] font-dmSans font-[600] text-white bg-gradient-to-r from-accent to-accent-bright shadow-lg shadow-indigo-500/20 hover:shadow-glow-sm hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:shadow-lg disabled:cursor-not-allowed transition-all duration-300"
          >
            Search customers
            <FontAwesomeIcon icon={faArrowRight} className="text-[13px]" />
          </button>
        </form>

        <div className="mt-5 pt-5 border-t border-edge-faint flex items-center gap-2 flex-wrap">
          <span className="text-[12px] font-semibold uppercase tracking-wider text-light-gray mr-1">
            Or browse
          </span>
          {STATUS_SHORTCUTS.map((item) => (
            <button
              key={item.route}
              type="button"
              onClick={() => navigate(item.route)}
              className="h-8 px-3 flex items-center gap-2 rounded-full border border-edge text-[12px] font-medium text-light-gray hover:text-text-primary hover:border-indigo-400/40 transition-all duration-200"
            >
              <FontAwesomeIcon icon={item.icon} className="text-[11px]" />
              {item.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CustomerSearch;
